import React from 'react';
import PropTypes from 'prop-types';
import { Popup } from 'semantic-ui-react';
import { Check, ChevronDown } from 'common/images/FaIcons';
import { useTranslation } from 'react-i18next';
import {
  FeatureDropdownWrapper,
  FeatureDropdownButton,
  FeatureDropdownText,
  FeatureDropdownMenu,
  FeatureDropdownHeader,
  FeatureCheckboxItem,
  FeatureCheckbox,
  FeatureCheckboxLabel,
  FeatureUnavailableBadge,
  DropdownOverlay
} from './styled';

const FeatureDropdown = ({
  isOpen,
  vibrationFeatures,
  speedBin,
  alarmThresholds,
  unavailableFeatures,
  checkVibrationFeatureThresholdsAvailable,
  getFeatureUnavailableReason,
  onToggle,
  onFeatureToggle
}) => {
  const { t } = useTranslation();
  const features = vibrationFeatures || [];
  const active = [];
  if (features.includes('acceleration')) active.push(t('sensors.eventDetection.coupling.acceleration'));
  if (features.includes('velocity')) active.push(t('sensors.eventDetection.coupling.velocity'));

  let displayText;
  if (active.length === 0) {
    displayText = t('sensors.eventDetection.coupling.selectFeatures');
  } else if (active.length === 1) {
    displayText = active[0];
  } else {
    displayText = t('sensors.eventDetection.coupling.selectedCount', { count: active.length });
  }

  const isFeatureUnavailable = feature => unavailableFeatures && unavailableFeatures.includes(feature);

  const handleFeatureToggle = (feature) => {
    // Don't allow toggling unavailable features
    if (isFeatureUnavailable(feature)) {
      return;
    }

    if (alarmThresholds && !checkVibrationFeatureThresholdsAvailable(feature, speedBin)) {
      return;
    }

    const currentFeatures = vibrationFeatures || [];
    const newFeatures = currentFeatures.includes(feature)
      ? currentFeatures.filter(f => f !== feature)
      : [...currentFeatures.filter(f => f !== feature), feature];

    const isVibrationCoupled = true; // This is only shown when vibration is coupled
    if (newFeatures.length === 0 && isVibrationCoupled) {
      return;
    }

    onFeatureToggle(newFeatures);
  };

  return (
    <FeatureDropdownWrapper>
      <FeatureDropdownButton onClick={onToggle}>
        <FeatureDropdownText>{displayText}</FeatureDropdownText>
        <ChevronDown />
      </FeatureDropdownButton>

      {isOpen && (
        <>
          <DropdownOverlay onClick={(e) => { e.stopPropagation(); onToggle(); }} />
          <FeatureDropdownMenu>
            <FeatureDropdownHeader>{t('sensors.eventDetection.coupling.vibrationFeatures')}</FeatureDropdownHeader>

            {/* Acceleration */}
            <Popup
              trigger={(
                <FeatureCheckboxItem
                  disabled={alarmThresholds && !checkVibrationFeatureThresholdsAvailable('acceleration', speedBin)}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFeatureToggle('acceleration');
                  }}
                >
                  <FeatureCheckbox
                    checked={features.includes('acceleration') && (!alarmThresholds || checkVibrationFeatureThresholdsAvailable('acceleration', speedBin))}
                    disabled={alarmThresholds && !checkVibrationFeatureThresholdsAvailable('acceleration', speedBin)}
                  >
                    {features.includes('acceleration') && <Check />}
                  </FeatureCheckbox>
                  <FeatureCheckboxLabel checked={features.includes('acceleration')}>
                    {t('sensors.eventDetection.coupling.acceleration')}
                  </FeatureCheckboxLabel>
                  {alarmThresholds && !checkVibrationFeatureThresholdsAvailable('acceleration', speedBin) && (
                    <FeatureUnavailableBadge>{t('sensors.eventDetection.coupling.notAvailable')}</FeatureUnavailableBadge>
                  )}
                </FeatureCheckboxItem>
              )}
              content={alarmThresholds && !checkVibrationFeatureThresholdsAvailable('acceleration', speedBin) ? getFeatureUnavailableReason('acceleration', speedBin) : t('sensors.eventDetection.coupling.accelerationHelp')}
              disabled={!alarmThresholds || checkVibrationFeatureThresholdsAvailable('acceleration', speedBin)}
              position="right center"
              size="small"
            />

            {/* Velocity */}
            <Popup
              trigger={(
                <FeatureCheckboxItem
                  disabled={alarmThresholds && !checkVibrationFeatureThresholdsAvailable('velocity', speedBin)}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFeatureToggle('velocity');
                  }}
                >
                  <FeatureCheckbox
                    checked={features.includes('velocity') && (!alarmThresholds || checkVibrationFeatureThresholdsAvailable('velocity', speedBin))}
                    disabled={alarmThresholds && !checkVibrationFeatureThresholdsAvailable('velocity', speedBin)}
                  >
                    {features.includes('velocity') && <Check />}
                  </FeatureCheckbox>
                  <FeatureCheckboxLabel checked={features.includes('velocity')}>
                    {t('sensors.eventDetection.coupling.velocity')}
                  </FeatureCheckboxLabel>
                  {alarmThresholds && !checkVibrationFeatureThresholdsAvailable('velocity', speedBin) && (
                    <FeatureUnavailableBadge>{t('sensors.eventDetection.coupling.notAvailable')}</FeatureUnavailableBadge>
                  )}
                </FeatureCheckboxItem>
              )}
              content={alarmThresholds && !checkVibrationFeatureThresholdsAvailable('velocity', speedBin) ? getFeatureUnavailableReason('velocity', speedBin) : t('sensors.eventDetection.coupling.velocityHelp')}
              disabled={!alarmThresholds || checkVibrationFeatureThresholdsAvailable('velocity', speedBin)}
              position="right center"
              size="small"
            />
          </FeatureDropdownMenu>
        </>
      )}
    </FeatureDropdownWrapper>
  );
};

FeatureDropdown.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  vibrationFeatures: PropTypes.array,
  speedBin: PropTypes.string.isRequired,
  alarmThresholds: PropTypes.object,
  unavailableFeatures: PropTypes.array,
  checkVibrationFeatureThresholdsAvailable: PropTypes.func.isRequired,
  getFeatureUnavailableReason: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  onFeatureToggle: PropTypes.func.isRequired
};

FeatureDropdown.defaultProps = {
  vibrationFeatures: [],
  alarmThresholds: null,
  unavailableFeatures: []
};

// Memoized because all function props are now wrapped in useCallback
export default React.memo(FeatureDropdown);
